import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  DollarSign,
  Eye,
  MousePointerClick,
  Target,
  RefreshCw,
  Plus,
  Search,
  Loader2,
  TrendingUp,
  MapPin,
  Clock,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Smartphone,
  Monitor,
  Tablet,
  Trophy,
  Zap,
  Copy,
  Check,
  Bug,
  Lightbulb,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import api, {
  GoogleAdsDashboard,
  GoogleAdsCampaign,
  GoogleAdsAccount,
  GoogleAdsKeyword,
  GoogleAdsSearchTerm,
  GoogleAdsConversionAction,
  GoogleAdsConversionMetric,
  GoogleAdsDevicePerformance,
  GoogleAdsGeoPerformance,
  GoogleAdsDayOfWeekPerformance,
  GoogleAdsHourPerformance,
  GoogleAdsImpressionShare,
  GoogleAdsAuctionInsight,
  GoogleAdsOptimizationAudit
} from '../services/api';
import { useClient } from '../contexts/ClientContext';
import CreateCampaignWizard from '../components/CreateCampaignWizard';

type TabType = 'overview' | 'campaigns' | 'keywords' | 'search_terms' | 'conversions' | 'performance' | 'competitive' | 'audit';

const GoogleAds = () => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<GoogleAdsDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<GoogleAdsCampaign[]>([]);
  const [keywords, setKeywords] = useState<GoogleAdsKeyword[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncing, setSyncing] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(30);

  // Analytics data states
  const [searchTermsData, setSearchTermsData] = useState<GoogleAdsSearchTerm[]>([]);
  const [conversionActions, setConversionActions] = useState<GoogleAdsConversionAction[]>([]);
  const [conversionMetrics, setConversionMetrics] = useState<GoogleAdsConversionMetric[]>([]);
  const [devicePerformance, setDevicePerformance] = useState<GoogleAdsDevicePerformance[]>([]);
  const [geoPerformance, setGeoPerformance] = useState<GoogleAdsGeoPerformance[]>([]);
  const [dayOfWeekPerformance, setDayOfWeekPerformance] = useState<GoogleAdsDayOfWeekPerformance[]>([]);
  const [hourPerformance, setHourPerformance] = useState<GoogleAdsHourPerformance[]>([]);
  const [impressionShare, setImpressionShare] = useState<GoogleAdsImpressionShare[]>([]);
  const [auctionInsights, setAuctionInsights] = useState<GoogleAdsAuctionInsight[]>([]);
  const [optimizationAudit, setOptimizationAudit] = useState<GoogleAdsOptimizationAudit | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [auditCopied, setAuditCopied] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<any>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [insights, setInsights] = useState<any>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [duplicateData, setDuplicateData] = useState<any>(null);
  const [duplicateLoading, setDuplicateLoading] = useState<string | null>(null);

  const runAuditDebug = async () => {
    if (!selectedAccount || selectedAccount === 'all') return;
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const result = await api.getGoogleAdsAuditDebug(selectedAccount, {
        days: dateRange,
        save_file: true
      });
      setDebugResult(result);
      setShowDebugPanel(true);
    } catch (err) {
      console.error('Debug failed:', err);
      setDebugResult({ errors: [err instanceof Error ? err.message : 'Debug failed'] });
      setShowDebugPanel(true);
    } finally {
      setDebugLoading(false);
    }
  };

  const loadInsights = async () => {
    if (!selectedAccount || selectedAccount === 'all') return;
    setInsightsLoading(true);
    try {
      const result = await api.getGoogleAdsOptimizationInsights(selectedAccount, {
        days: dateRange
      });
      setInsights(result);
    } catch (err) {
      console.error('Failed to load insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const copyAuditToClipboard = () => {
    if (optimizationAudit) {
      navigator.clipboard.writeText(JSON.stringify(optimizationAudit, null, 2));
      setAuditCopied(true);
      setTimeout(() => setAuditCopied(false), 2000);
    }
  };

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGoogleAdsDashboard(selectedClientId || undefined);
      setDashboard(data);

      // Auto-select account based on client context
      if (selectedClientId && data.accounts_list.length > 0) {
        // When viewing a specific client, auto-select their first (or only) account
        setSelectedAccount(data.accounts_list[0].id);
      } else if (!selectedClientId && data.accounts_list.length === 1) {
        // When viewing all clients but only one account exists, auto-select it
        setSelectedAccount(data.accounts_list[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  // Reset selected account when client changes
  useEffect(() => {
    setSelectedAccount('all');
    // Clear analytics data when client changes
    setSearchTermsData([]);
    setConversionActions([]);
    setConversionMetrics([]);
    setDevicePerformance([]);
    setGeoPerformance([]);
    setDayOfWeekPerformance([]);
    setHourPerformance([]);
    setImpressionShare([]);
    setAuctionInsights([]);
    setOptimizationAudit(null);
  }, [selectedClientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
    } else if (activeTab === 'keywords') {
      loadKeywords();
    } else if (activeTab === 'search_terms' && selectedAccount !== 'all') {
      loadSearchTerms();
    } else if (activeTab === 'conversions' && selectedAccount !== 'all') {
      loadConversions();
    } else if (activeTab === 'performance' && selectedAccount !== 'all') {
      loadPerformanceData();
    } else if (activeTab === 'competitive' && selectedAccount !== 'all') {
      loadCompetitiveData();
    } else if (activeTab === 'audit' && selectedAccount !== 'all') {
      loadAuditData();
      loadInsights();
    }
  }, [activeTab, selectedAccount, statusFilter, searchTerm, selectedClientId, dateRange]);

  const loadCampaigns = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedAccount !== 'all') params.account = selectedAccount;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getGoogleAdsCampaigns(params);
      setCampaigns(response.results);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
  };

  const loadKeywords = async () => {
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.getGoogleAdsKeywords(params);
      setKeywords(response.results);
    } catch (err) {
      console.error('Failed to load keywords:', err);
    }
  };

  const loadSearchTerms = async () => {
    if (selectedAccount === 'all') return;
    try {
      setAnalyticsLoading(true);
      const response = await api.getGoogleAdsSearchTerms(selectedAccount, { days: dateRange });
      setSearchTermsData(response.search_terms || []);
    } catch (err) {
      console.error('Failed to load search terms:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadConversions = async () => {
    if (selectedAccount === 'all') return;
    try {
      setAnalyticsLoading(true);
      const [actionsRes, metricsRes] = await Promise.all([
        api.getGoogleAdsConversionActions(selectedAccount),
        api.getGoogleAdsConversionMetrics(selectedAccount, { days: dateRange })
      ]);
      setConversionActions(actionsRes.conversion_actions || []);
      setConversionMetrics(metricsRes.metrics || []);
    } catch (err) {
      console.error('Failed to load conversions:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadPerformanceData = async () => {
    if (selectedAccount === 'all') return;
    try {
      setAnalyticsLoading(true);
      const [deviceRes, geoRes, dayRes, hourRes] = await Promise.all([
        api.getGoogleAdsPerformanceByDevice(selectedAccount, { days: dateRange }),
        api.getGoogleAdsPerformanceByGeo(selectedAccount, { days: dateRange }),
        api.getGoogleAdsPerformanceByDayOfWeek(selectedAccount, { days: dateRange }),
        api.getGoogleAdsPerformanceByHour(selectedAccount, { days: dateRange })
      ]);
      setDevicePerformance(deviceRes.data || []);
      setGeoPerformance(geoRes.data || []);
      setDayOfWeekPerformance(dayRes.data || []);
      setHourPerformance(hourRes.data || []);
    } catch (err) {
      console.error('Failed to load performance data:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadCompetitiveData = async () => {
    if (selectedAccount === 'all') return;
    try {
      setAnalyticsLoading(true);
      const [shareRes, insightsRes] = await Promise.all([
        api.getGoogleAdsImpressionShare(selectedAccount, { days: dateRange }),
        api.getGoogleAdsAuctionInsights(selectedAccount, { days: dateRange })
      ]);
      setImpressionShare(shareRes.data || []);
      setAuctionInsights(insightsRes.insights || []);
    } catch (err) {
      console.error('Failed to load competitive data:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadAuditData = async () => {
    if (selectedAccount === 'all') return;
    try {
      setAnalyticsLoading(true);
      const response = await api.getGoogleAdsOptimizationAudit(selectedAccount, { days: dateRange });
      setOptimizationAudit(response);
    } catch (err) {
      console.error('Failed to load audit data:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSync = async (accountId: string) => {
    try {
      setSyncing(accountId);
      await api.syncGoogleAdsAccount(accountId);
      await loadDashboard();
      if (activeTab === 'campaigns') {
        await loadCampaigns();
      }
    } catch (err) {
      console.error('Failed to sync account:', err);
    } finally {
      setSyncing(null);
    }
  };

  const handleConnectAccount = async () => {
    try {
      const { auth_url } = await api.getGoogleAdsAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      setError('Failed to initiate Google Ads connection');
    }
  };

  const handleDuplicate = async (campaignId: string) => {
    try {
      setDuplicateLoading(campaignId);
      const blueprint = await api.getGoogleAdsCampaignBlueprint(campaignId);
      setDuplicateData(blueprint);
      setShowCreateWizard(true);
    } catch (err) {
      console.error('Failed to load campaign blueprint:', err);
    } finally {
      setDuplicateLoading(null);
    }
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${(num || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  const formatPercent = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${(num || 0).toFixed(2)}%`;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'enabled':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'removed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getCampaignTypeClass = (type: string) => {
    switch (type) {
      case 'search':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'display':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'shopping':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'video':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'performance_max':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="w-5 h-5" />;
      case 'desktop':
        return <Monitor className="w-5 h-5" />;
      case 'tablet':
        return <Tablet className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getSeverityClass = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'keywords', label: 'Keywords' },
    { id: 'search_terms', label: 'Search Terms' },
    { id: 'conversions', label: 'Conversions' },
    { id: 'performance', label: 'Performance' },
    { id: 'competitive', label: 'Competitive' },
    { id: 'audit', label: 'Audit' }
  ];

  const needsAccountSelection = ['search_terms', 'conversions', 'performance', 'competitive', 'audit'].includes(activeTab);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">Google Ads</h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage and analyze your advertising campaigns
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(parseInt(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <button
                onClick={loadDashboard}
                disabled={loading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {selectedAccount !== 'all' && dashboard?.accounts_list.find(a => a.id === selectedAccount) && (
                <button
                  onClick={() => setShowCreateWizard(true)}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create Campaign
                </button>
              )}
              <button
                onClick={handleConnectAccount}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Connect Account
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={loadDashboard}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Account Selector for Analytics Tabs */}
        {needsAccountSelection && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-2">
            <div className="flex items-center gap-4">
              <label className="text-gray-700 dark:text-gray-300 font-medium">
                {selectedClientId ? 'Account:' : 'Select Account:'}
              </label>
              {/* Show read-only display when client has single account (auto-selected) */}
              {selectedClientId && dashboard?.accounts_list.length === 1 ? (
                <div className="flex-1 max-w-md px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="text-gray-900 dark:text-white font-medium">
                    {dashboard.accounts_list[0].name}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 ml-2">
                    ({dashboard.accounts_list[0].customer_id})
                  </span>
                </div>
              ) : (
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="flex-1 max-w-md border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                >
                  {!selectedClientId && <option value="all">-- Select an account --</option>}
                  {dashboard?.accounts_list.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.customer_id})
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedAccount === 'all' && !selectedClientId && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                Please select an account to view analytics data
              </p>
            )}
            {!selectedClientId && dashboard?.accounts_list.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                No Google Ads accounts found. Connect accounts from the Overview tab.
              </p>
            )}
            {selectedClientId && dashboard?.accounts_list.length === 0 && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                This client has no linked Google Ads accounts.
              </p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-black'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatCurrency(dashboard.accounts.total_spend)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Total Spend</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Eye className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(dashboard.accounts.total_impressions)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Impressions</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <MousePointerClick className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(dashboard.accounts.total_clicks)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Clicks</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-5 h-5 text-primary-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(dashboard.accounts.total_conversions)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Conversions</p>
              </div>
            </div>

            {/* Connected Accounts */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Connected Accounts</h2>
              </div>
              <div className="p-4">
                {dashboard.accounts_list.length === 0 ? (
                  <div className="text-center py-3">
                    <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No Google Ads accounts connected</p>
                    <button
                      onClick={handleConnectAccount}
                      className="mt-4 text-primary-500 hover:text-primary-600"
                    >
                      Connect your first account
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.accounts_list.map((account) => (
                      <AccountCard
                        key={account.id}
                        account={account}
                        onSync={() => handleSync(account.id)}
                        syncing={syncing === account.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Campaigns */}
            {dashboard.recent_campaigns.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Campaigns</h2>
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className="text-sm text-primary-500 hover:text-primary-600"
                  >
                    View all
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Campaign</th>
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Type</th>
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Status</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Spend</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recent_campaigns.map((campaign) => (
                        <tr key={campaign.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{campaign.client_name}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getCampaignTypeClass(campaign.campaign_type)}`}>
                              {campaign.campaign_type}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatCurrency(campaign.cost)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(campaign.clicks)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatPercent(campaign.ctr)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search campaigns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  >
                    <option value="all">All Accounts</option>
                    {dashboard?.accounts_list.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  >
                    <option value="all">All Status</option>
                    <option value="enabled">Enabled</option>
                    <option value="paused">Paused</option>
                    <option value="removed">Removed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Campaigns Table */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Campaign</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Type</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Status</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Budget</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Spend</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Impressions</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conv.</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          No campaigns found
                        </td>
                      </tr>
                    ) : (
                      campaigns.map((campaign) => (
                        <tr key={campaign.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{campaign.account_name}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getCampaignTypeClass(campaign.campaign_type)}`}>
                              {campaign.campaign_type}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatCurrency(campaign.daily_budget)}/day
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatCurrency(campaign.cost)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(campaign.impressions)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(campaign.clicks)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatPercent(campaign.ctr)}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">
                            {formatNumber(campaign.conversions)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <button
                              onClick={() => handleDuplicate(campaign.id)}
                              disabled={duplicateLoading === campaign.id}
                              className="text-xs text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-400 disabled:opacity-50"
                              title="Duplicate campaign"
                            >
                              {duplicateLoading === campaign.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Keywords Tab */}
        {activeTab === 'keywords' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search keywords..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="enabled">Enabled</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Keyword</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Match</th>
                      <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Status</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">QS</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Impr.</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Avg CPC</th>
                      <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          No keywords found
                        </td>
                      </tr>
                    ) : (
                      keywords.map((keyword) => (
                        <tr key={keyword.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{keyword.text}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{keyword.campaign_name}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                              {keyword.match_type}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClass(keyword.status)}`}>
                              {keyword.status}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            {keyword.quality_score ? (
                              <span className={`font-medium ${
                                keyword.quality_score >= 7 ? 'text-green-600 dark:text-green-400' :
                                keyword.quality_score >= 4 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {keyword.quality_score}/10
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(keyword.impressions)}</td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(keyword.clicks)}</td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatPercent(keyword.ctr)}</td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(keyword.average_cpc)}</td>
                          <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(keyword.cost)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Search Terms Tab */}
        {activeTab === 'search_terms' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : selectedAccount === 'all' ? (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select an account to view search terms</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Search Terms Report</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Actual search queries that triggered your ads</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Search Term</th>
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Matched Keyword</th>
                        <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Match Type</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Impr.</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Cost</th>
                        <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conv.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchTermsData.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            No search terms data available
                          </td>
                        </tr>
                      ) : (
                        searchTermsData.map((term, idx) => (
                          <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="py-4 px-6 font-medium text-gray-900 dark:text-white">{term.search_term}</td>
                            <td className="py-4 px-6 text-gray-600 dark:text-gray-400">{term.keyword_text}</td>
                            <td className="py-4 px-6">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                                {term.match_type}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(term.impressions)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(term.clicks)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatPercent(term.ctr)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(term.cost)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(term.conversions)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conversions Tab */}
        {activeTab === 'conversions' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : selectedAccount === 'all' ? (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Target className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select an account to view conversion data</p>
              </div>
            ) : (
              <>
                {/* Conversion Metrics */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Conversion Performance</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Performance metrics by conversion action</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Conversion Action</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conversions</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Value</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Cost/Conv.</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conv. Rate</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Value/Conv.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversionMetrics.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                              No conversion metrics available
                            </td>
                          </tr>
                        ) : (
                          conversionMetrics.map((metric, idx) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-4 px-6 font-medium text-gray-900 dark:text-white">{metric.conversion_action}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(metric.conversions)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(metric.conversion_value)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(metric.cost_per_conversion)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatPercent(metric.conversion_rate)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(metric.value_per_conversion)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Conversion Actions */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Conversion Actions</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Configured conversion tracking actions</p>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {conversionActions.length === 0 ? (
                      <div className="col-span-full text-center py-3 text-gray-500 dark:text-gray-400">
                        No conversion actions configured
                      </div>
                    ) : (
                      conversionActions.map((action, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{action.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{action.category}</p>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              action.status === 'ENABLED' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              {action.status}
                            </span>
                          </div>
                          <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                            <p>Type: {action.type}</p>
                            <p>Attribution: {action.attribution_model}</p>
                            {action.include_in_conversions && (
                              <p className="text-green-600 dark:text-green-400 mt-1">Included in Conversions column</p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : selectedAccount === 'all' ? (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select an account to view performance data</p>
              </div>
            ) : (
              <>
                {/* Device Performance */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Performance by Device</h2>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {devicePerformance.length === 0 ? (
                      <div className="col-span-full text-center py-3 text-gray-500 dark:text-gray-400">
                        No device data available
                      </div>
                    ) : (
                      devicePerformance.map((device, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-3 mb-3">
                            {getDeviceIcon(device.device)}
                            <span className="font-medium text-gray-900 dark:text-white capitalize">{device.device}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">Clicks</p>
                              <p className="font-medium text-gray-900 dark:text-white">{formatNumber(device.clicks)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">CTR</p>
                              <p className="font-medium text-gray-900 dark:text-white">{formatPercent(device.ctr)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">Cost</p>
                              <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(device.cost)}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">Conv. Rate</p>
                              <p className="font-medium text-gray-900 dark:text-white">{formatPercent(device.conversion_rate)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Day of Week Performance */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Performance by Day of Week</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Day</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Impressions</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Cost</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conv.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayOfWeekPerformance.map((day, idx) => (
                          <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="py-4 px-6 font-medium text-gray-900 dark:text-white">{day.day_of_week}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(day.impressions)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(day.clicks)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatPercent(day.ctr)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(day.cost)}</td>
                            <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(day.conversions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Hour Performance */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Performance by Hour</h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                      {hourPerformance.map((hour, idx) => {
                        const maxClicks = Math.max(...hourPerformance.map(h => h.clicks));
                        const intensity = maxClicks > 0 ? hour.clicks / maxClicks : 0;
                        return (
                          <div
                            key={idx}
                            className="p-2 rounded-lg text-center transition-colors"
                            style={{
                              backgroundColor: `rgba(34, 197, 94, ${0.1 + intensity * 0.5})`
                            }}
                            title={`${hour.hour}:00 - Clicks: ${hour.clicks}, CTR: ${formatPercent(hour.ctr)}`}
                          >
                            <p className="text-xs text-gray-600 dark:text-gray-400">{hour.hour}:00</p>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">{hour.clicks}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Geographic Performance */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Performance by Location</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-4 px-6 font-medium text-gray-900 dark:text-white">Location</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Impressions</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Clicks</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">CTR</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Cost</th>
                          <th className="text-right py-4 px-6 font-medium text-gray-900 dark:text-white">Conv.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {geoPerformance.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                              No geographic data available
                            </td>
                          </tr>
                        ) : (
                          geoPerformance.slice(0, 20).map((geo, idx) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-4 px-6">
                                <p className="font-medium text-gray-900 dark:text-white">{geo.city || geo.region || geo.country}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{[geo.region, geo.country].filter(Boolean).join(', ')}</p>
                              </td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(geo.impressions)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(geo.clicks)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatPercent(geo.ctr)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatCurrency(geo.cost)}</td>
                              <td className="py-4 px-6 text-right text-gray-900 dark:text-white">{formatNumber(geo.conversions)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Competitive Tab */}
        {activeTab === 'competitive' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : selectedAccount === 'all' ? (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select an account to view competitive data</p>
              </div>
            ) : (
              <>
                {/* Impression Share */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Impression Share Diagnostics</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">How often your ads show vs. potential impressions</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Campaign</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Search IS</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Top IS</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Abs Top IS</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Lost (Budget)</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Lost (Rank)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impressionShare.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                              No impression share data available
                            </td>
                          </tr>
                        ) : (
                          impressionShare.map((share, idx) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-3 px-4 font-medium text-gray-900 dark:text-white truncate max-w-[200px]" title={share.campaign_name}>{share.campaign_name}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(share.search_impression_share)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(share.search_top_impression_share)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(share.search_absolute_top_impression_share)}</td>
                              <td className="py-3 px-3 text-right text-red-600 dark:text-red-400">{formatPercent(share.search_budget_lost_impression_share)}</td>
                              <td className="py-3 px-3 text-right text-yellow-600 dark:text-yellow-400">{formatPercent(share.search_rank_lost_impression_share)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Auction Insights */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Auction Insights</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Compare your performance against competitors</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Competitor</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Impr. Share</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Overlap Rate</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Position Above</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Top of Page</th>
                          <th className="text-right py-3 px-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">Outranking</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auctionInsights.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                              No auction insights data available
                            </td>
                          </tr>
                        ) : (
                          auctionInsights.map((insight, idx) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-3 px-4 font-medium text-gray-900 dark:text-white truncate max-w-[200px]" title={insight.display_domain}>{insight.display_domain}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(insight.impression_share)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(insight.overlap_rate)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(insight.position_above_rate)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(insight.top_of_page_rate)}</td>
                              <td className="py-3 px-3 text-right text-gray-900 dark:text-white">{formatPercent(insight.outranking_share)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Audit Tab */}
        {/* Audit Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : selectedAccount === 'all' ? (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Zap className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Select an account to run optimization audit</p>
              </div>
            ) : optimizationAudit ? (
              <>
                {/* Audit Action Buttons */}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={runAuditDebug}
                    disabled={debugLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {debugLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Running Debug...</span>
                      </>
                    ) : (
                      <>
                        <Bug className="w-4 h-4" />
                        <span>Run Debug</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={copyAuditToClipboard}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      auditCopied
                        ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {auditCopied ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy Audit JSON</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Debug Results Panel */}
                {showDebugPanel && debugResult && (
                  <div className="bg-gray-900 dark:bg-gray-950 rounded-lg border border-gray-700 p-4 overflow-hidden">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Bug className="w-5 h-5" />
                        Data Integrity Debug Results
                      </h3>
                      <button
                        onClick={() => setShowDebugPanel(false)}
                        className="text-gray-400 hover:text-white"
                      >
                        &times;
                      </button>
                    </div>

                    {/* Errors */}
                    {debugResult.errors?.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-red-400 font-medium text-sm mb-1">Errors ({debugResult.errors.length})</h4>
                        <ul className="text-red-300 text-xs space-y-1 bg-red-900/20 p-2 rounded">
                          {debugResult.errors.map((err: string, i: number) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Warnings */}
                    {debugResult.warnings?.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-yellow-400 font-medium text-sm mb-1">Warnings ({debugResult.warnings.length})</h4>
                        <ul className="text-yellow-300 text-xs space-y-1 bg-yellow-900/20 p-2 rounded">
                          {debugResult.warnings.map((warn: string, i: number) => (
                            <li key={i}>{warn}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Spend Reconciliation */}
                    {debugResult.reconciliation?.spend && (
                      <div className="mb-3">
                        <h4 className="text-blue-400 font-medium text-sm mb-1">Spend Reconciliation</h4>
                        <div className="text-gray-300 text-xs bg-gray-800 p-2 rounded font-mono">
                          <div>Account: ${debugResult.reconciliation.spend.account_spend_currency?.toFixed(2)}</div>
                          <div>Campaigns Sum: ${debugResult.reconciliation.spend.campaigns_sum_spend_currency?.toFixed(2)}</div>
                          <div>Diff: ${debugResult.reconciliation.spend.account_vs_campaigns_diff_currency?.toFixed(2)}</div>
                        </div>
                      </div>
                    )}

                    {/* Conversion Reconciliation */}
                    {debugResult.reconciliation?.conversions?.account && (
                      <div className="mb-3">
                        <h4 className="text-green-400 font-medium text-sm mb-1">Conversion Validation</h4>
                        <div className="text-gray-300 text-xs bg-gray-800 p-2 rounded font-mono">
                          <div>Conversions: {debugResult.reconciliation.conversions.account.conversions?.toFixed(2)}</div>
                          <div>Conversion Value: ${debugResult.reconciliation.conversions.account.conversions_value?.toFixed(2)}</div>
                          <div>All Conversions: {debugResult.reconciliation.conversions.account.all_conversions?.toFixed(2)}</div>
                        </div>
                      </div>
                    )}

                    {/* Copy Debug JSON */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(debugResult, null, 2));
                      }}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copy Full Debug JSON
                    </button>

                    {debugResult.debug_file_path && (
                      <p className="text-xs text-gray-500 mt-2">
                        Saved to: {debugResult.debug_file_path}
                      </p>
                    )}
                  </div>
                )}

                {/* Optimization Insights */}
                {insights && insights.insights?.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                          Optimization Insights ({insights.insights_count})
                        </h2>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600 dark:text-green-400">
                          {insights.automation_safe_count} automation-safe
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {insights.insights_by_impact?.HIGH || 0} high impact
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                      {insights.insights.map((insight: any, idx: number) => (
                        <div key={idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 px-2 py-1 rounded text-xs font-medium ${
                              insight.expected_impact === 'HIGH'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : insight.expected_impact === 'MEDIUM'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {insight.expected_impact}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {insight.entity_name || insight.scope}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                  [{insight.rule_id}]
                                </span>
                                {insight.automation_safe && (
                                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                                    Auto-safe
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {insight.observation}
                              </p>
                              <div className="text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-2 rounded">
                                <strong>Recommendation:</strong> {insight.recommendation}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {insightsLoading && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-500 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Analyzing optimization opportunities...</p>
                  </div>
                )}

                {/* Audit Validation Status */}
                {(optimizationAudit as any)?.validation && (
                  <div className={`rounded-lg border p-4 ${
                    (optimizationAudit as any)?.validation?.passed === false
                      ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700'
                      : (optimizationAudit as any)?.validation?.warnings?.length > 0
                        ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700'
                        : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {(optimizationAudit as any)?.validation?.passed === false ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      ) : (optimizationAudit as any)?.validation?.warnings?.length > 0 ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      <span className={`font-medium ${
                        (optimizationAudit as any)?.validation?.passed === false
                          ? 'text-red-700 dark:text-red-300'
                          : (optimizationAudit as any)?.validation?.warnings?.length > 0
                            ? 'text-yellow-700 dark:text-yellow-300'
                            : 'text-green-700 dark:text-green-300'
                      }`}>
                        {(optimizationAudit as any)?.validation?.passed === false
                          ? 'Data Validation Failed'
                          : (optimizationAudit as any)?.validation?.warnings?.length > 0
                            ? 'Data Validated with Warnings'
                            : 'Data Validated Successfully'}
                      </span>
                    </div>
                    {(optimizationAudit as any)?.validation?.errors?.length > 0 && (
                      <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 ml-7">
                        {(optimizationAudit as any)?.validation?.errors?.map((err: string, i: number) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}
                    {(optimizationAudit as any)?.validation?.warnings?.length > 0 && (
                      <ul className="text-sm text-yellow-600 dark:text-yellow-400 space-y-1 ml-7 mt-1">
                        {(optimizationAudit as any)?.validation?.warnings?.map((warn: string, i: number) => (
                          <li key={i}>{warn}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Account Summary */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Account Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Total Spend</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency((optimizationAudit as any)?.account_summary?.spend ?? 0)}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Impressions</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{formatNumber((optimizationAudit as any)?.account_summary?.impressions ?? 0)}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Clicks</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{formatNumber((optimizationAudit as any)?.account_summary?.clicks ?? 0)}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Conversions</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{formatNumber((optimizationAudit as any)?.account_summary?.conversions_primary ?? 0)}</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">Cost per Conv.</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency((optimizationAudit as any)?.account_summary?.cpa ?? 0)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-xs">CTR</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{formatPercent((optimizationAudit as any)?.account_summary?.ctr ?? 0)}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-xs">Avg CPC</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency((optimizationAudit as any)?.account_summary?.avg_cpc ?? 0)}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-xs">Conv. Value</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency((optimizationAudit as any)?.account_summary?.conversion_value ?? 0)}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-600 dark:text-gray-400 text-xs">ROAS</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{((optimizationAudit as any)?.account_summary?.roas ?? 0).toFixed(2)}x</p>
                    </div>
                  </div>
                </div>

                {/* Campaigns Performance */}
                {(optimizationAudit as any)?.campaigns?.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Campaign Analysis ({(optimizationAudit as any)?.campaigns?.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Campaign</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Status</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Spend</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Conv.</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">CPA</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Impr. Share</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Lost (Budget)</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Lost (Rank)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(optimizationAudit as any)?.campaigns?.map((camp: any, idx: number) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-3 px-4">
                                <p className="font-medium text-gray-900 dark:text-white">{camp.campaign_name}</p>
                                <p className="text-xs text-gray-500">{camp.channel}</p>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(camp.status)}`}>
                                  {camp.status}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{formatCurrency(camp.spend)}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{camp.conversions_primary}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{formatCurrency(camp.cpa)}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{formatPercent(camp.impression_share * 100)}</td>
                              <td className="py-3 px-4 text-right text-red-600 dark:text-red-400">{formatPercent(camp.lost_is_budget * 100)}</td>
                              <td className="py-3 px-4 text-right text-yellow-600 dark:text-yellow-400">{formatPercent(camp.lost_is_rank * 100)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Search Terms - Top Performers */}
                {(optimizationAudit as any)?.search_terms?.top_by_conversions?.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Top Converting Search Terms</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Search Term</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Campaign</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Conv.</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Spend</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">CPA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(optimizationAudit as any)?.search_terms?.top_by_conversions?.slice(0, 10).map((st: any, idx: number) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                              <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{st.search_term}</td>
                              <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{st.campaign_name}</td>
                              <td className="py-3 px-4 text-right text-green-600 dark:text-green-400 font-medium">{st.conversions}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{formatCurrency(st.cost)}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{st.conversions > 0 ? formatCurrency(st.cost / st.conversions) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Search Terms - Potential Negatives */}
                {(optimizationAudit as any)?.search_terms?.worst_cpa?.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Potential Negative Keywords (No Conversions)</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Search Term</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Campaign</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Clicks</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Wasted Spend</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(optimizationAudit as any)?.search_terms?.worst_cpa?.slice(0, 10).map((st: any, idx: number) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                              <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{st.search_term}</td>
                              <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{st.campaign_name}</td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">{st.clicks}</td>
                              <td className="py-3 px-4 text-right text-red-600 dark:text-red-400 font-medium">{formatCurrency(st.spend)}</td>
                              <td className="py-3 px-4">
                                {st.recommended_negative && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                    Add as Negative
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Date Range Info */}
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                  Data from {(optimizationAudit as any)?.date_range?.start} to {(optimizationAudit as any)?.date_range?.end} ({(optimizationAudit as any)?.date_range?.days} days)
                </div>
              </>
            ) : (
              <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Zap className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No audit data available</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Create Campaign Wizard */}
      {showCreateWizard && selectedAccount !== 'all' && dashboard?.accounts_list.find(a => a.id === selectedAccount) && (
        <CreateCampaignWizard
          account={dashboard.accounts_list.find(a => a.id === selectedAccount)!}
          onClose={() => { setShowCreateWizard(false); setDuplicateData(null); }}
          onSuccess={() => {
            loadDashboard();
            if (activeTab === 'campaigns') loadCampaigns();
          }}
          initialData={duplicateData || undefined}
        />
      )}
    </div>
  );
};

// Account Card Component
const AccountCard = ({ account, onSync, syncing }: {
  account: GoogleAdsAccount;
  onSync: () => void;
  syncing: boolean;
}) => {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{account.name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {account.customer_id} | {account.client_name || 'No client linked'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right">
          <p className="font-medium text-gray-900 dark:text-white">
            ${(parseFloat(account.total_spend) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Spend</p>
        </div>
        <div className="text-right">
          <p className="font-medium text-gray-900 dark:text-white">{account.total_clicks.toLocaleString()}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Clicks</p>
        </div>
        <div className="text-right">
          <p className="font-medium text-gray-900 dark:text-white">{account.campaigns_count}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Campaigns</p>
        </div>
        <button
          onClick={onSync}
          disabled={syncing}
          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          title="Sync account"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${syncing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default GoogleAds;
